import {useCallback} from "react";
import {saveAs} from "file-saver";

import useApi from "../services/api/hooks/useApi";
import ApiHttpVerbs from "../services/api/types/ApiHttpVerbs";
import Api from "../services/auth/types/IsaacApi";
import type ApiResponse from "../services/api/types/ApiResponse";
import type {ModifiedConstructionNewElements} from "../types/ModifiedConstructionNewElements";
import IsaacToastWrapperType from "../types/IsaacToastWrapperType";
import type ICalculationNewHook from "../types/ICalculationNewHook";
import type CalculationListItemDto from "../dto/calculations/CalculationListItemDto";
import type CalculationResultDto from "../dto/calculations/CalculationResultDto";
import type { CalculationConstructionDetailsResponseDto } from "../dto/calculations/CalculationConstructionDetailsDto";
import type FileData from "../dto/calculations/FileData";
import type CalculationNoteMatGroundType from "../dto/calculations/CalculationNoteMatGroundTypeDto";
import type CalculationNoteFoundationLoadCase from "../dto/calculations/CalculationNoteFoundationLoadCase";
import type DrillholeDto from "../dto/calculations/DrillholeDto";

/** Hook that defines all the calls for the Calculation entity. */
export const useCalculationNewHook = (): ICalculationNewHook => {
  const {fetchData} = useApi();

  const deleteCalculationByConstructionId = useCallback(async (
    constructionId: string,
    correlationGuid: string,
  ): Promise<ApiResponse<void>> => {
    const url = `${process.env.REACT_APP_ISAAC_API_URL}/calculations/byConstruction/${constructionId}`;
    const response = await fetchData<never>(
      url,
      Api.ISAAC,
      {handlerType: IsaacToastWrapperType.Full},
      ApiHttpVerbs.DELETE,
      {
        "X-Correlation-ID": correlationGuid,
      },
    );
    return response;
  }, [fetchData]);

  const getCalculationsByConstructionId = useCallback(async (id: string): Promise<ApiResponse<CalculationListItemDto[]>> => {
    const url = `${process.env.REACT_APP_ISAAC_API_URL}/calculations/byconstruction/${id}`;
    const response = await fetchData<CalculationListItemDto[]>(url, Api.ISAAC);
    return response;
  }, [fetchData]);

  const getCalculationResults = useCallback(async (calculationId: string): Promise<ApiResponse<CalculationResultDto>> => {
    const url = `${process.env.REACT_APP_ISAAC_API_URL}/calculations/${calculationId}/result`;
    const response = await fetchData<CalculationResultDto>(url, Api.ISAAC);
    return response;
  }, [fetchData]);

  const stopCalculationById = useCallback(async (calculationId: string): Promise<ApiResponse<void>> => {
    const url = `${process.env.REACT_APP_ISAAC_API_URL}/calculations/${calculationId}/interrupt`;
    const response = await fetchData<never>(url, Api.ISAAC, {useGlobalHandler: false}, ApiHttpVerbs.POST);
    return response;
  }, [fetchData]);

  const getLatestCalculationByConstructionId = useCallback(async (id: string): Promise<ApiResponse<CalculationListItemDto>> => {
    const url = `${process.env.REACT_APP_ISAAC_API_URL}/calculations/byConstruction/${id}/latest`;
    const response = await fetchData<CalculationListItemDto>(url, Api.ISAAC);
    return response;
  }, [fetchData]);

  const getAcceptedCalculationByConstructionId = useCallback(async (id: string): Promise<ApiResponse<CalculationListItemDto>> => {
    const url = `${process.env.REACT_APP_ISAAC_API_URL}/calculations/byConstruction/${id}/accepted`;
    const response = await fetchData<CalculationListItemDto>(url, Api.ISAAC);
    return response;
  }, [fetchData]);

  const getConstructionDetails = useCallback(async (
    calculationId: string,
  ): Promise<ApiResponse<CalculationConstructionDetailsResponseDto>> => {
    const url = `${process.env.REACT_APP_ISAAC_API_URL}/calculations/${calculationId}/constructiondetails`;
    return await fetchData<CalculationConstructionDetailsResponseDto>(url, Api.ISAAC);
  }, [fetchData]);

  const createStartCalculation = useCallback(async (
    constructionId: string,
    correlationGuid: string,
    collection?: ModifiedConstructionNewElements | null,
  ): Promise<ApiResponse<string>> => {
    const url = `${process.env.REACT_APP_ICC_API_URL}/constructions/${constructionId}/validateeditedconstructiongeometricallyandstartcalculation`;

    const response = await fetchData<string>(
      url,
      Api.ICC,
      {handlerType: IsaacToastWrapperType.Full},
      ApiHttpVerbs.POST,
      {
        "X-Correlation-ID": correlationGuid,
      },
      JSON.stringify(collection),
    );
    return response;
  }, [fetchData]);

  const deleteCalculationById = useCallback(async (id: string, correlationId: string): Promise<ApiResponse<void>> => {
    const url = `${process.env.REACT_APP_ISAAC_API_URL}/calculations/${id}`;

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    const response = await fetchData<void>(url, Api.ISAAC, {useGlobalHandler: false}, ApiHttpVerbs.DELETE, {
      "X-Correlation-ID": correlationId,
    });
    return response;
  }, [fetchData]);

  const getDataAndSaveToFile = useCallback(async (url: string): Promise<ApiResponse<FileData>> => {
    const response = await fetchData<FileData>(url, Api.ISAAC, {
      handlerType: IsaacToastWrapperType.Full,
    });

    if (response.success && response.data?.blob) {
      saveAs(response.data.blob, response.data.fileName);
    }

    return response;
  }, [fetchData]);

  const downloadExcelInputMatrixById = useCallback(async (calculationId: string): Promise<ApiResponse<FileData>> => {
    const url = `${process.env.REACT_APP_ISAAC_API_URL}/calculations/${calculationId}/inputMatrix`;

    return await getDataAndSaveToFile(url);
  }, [getDataAndSaveToFile]);

  const downloadJsonInputMatrixById = useCallback(async (calculationId: string): Promise<ApiResponse<FileData>> => {
    const url = `${process.env.REACT_APP_ISAAC_API_URL}/calculations/${calculationId}/inputMatrixJson`;

    return await getDataAndSaveToFile(url);
  }, [getDataAndSaveToFile]);

  const downloadJsonCalculationConstructionById = useCallback(async (calculationId: string): Promise<ApiResponse<FileData>> => {
    const url = `${process.env.REACT_APP_ISAAC_API_URL}/calculations/${calculationId}/constructionJson`;

    return await getDataAndSaveToFile(url);
  }, [getDataAndSaveToFile]);

  const getCalculationNoteMatGroundTypesById = useCallback(async (
    calculationId: string,
  ): Promise<ApiResponse<CalculationNoteMatGroundType[]>> => {
    const url = `${process.env.REACT_APP_ISAAC_API_URL}/calculations/${calculationId}/noteMatGroundTypes`;

    const response = await fetchData<CalculationNoteMatGroundType[]>(url, Api.ISAAC, {useGlobalHandler: false});

    return response;
  }, [fetchData]);

  const validateDrillholesCode = useCallback(async (drillholes: DrillholeDto[]): Promise<ApiResponse<void>> => {
    const url = `${process.env.REACT_APP_ISAAC_API_URL}/calculations/validateDrillholesCode`;
    const response = await fetchData<never>(
      url,
      Api.ISAAC,
      {useGlobalHandler: false},
      ApiHttpVerbs.POST,
      {},
      JSON.stringify({drillholes}),
    );

    return response;
  }, [fetchData]);

  const updateDrillholesCode = useCallback(async (
    selectedCalculationId: string,
    mastId: string,
    drillholes: DrillholeDto[],
  ): Promise<ApiResponse<void>> => {
    const url = `${process.env.REACT_APP_ISAAC_API_URL}/calculations/${selectedCalculationId}/drillholesCode`;
    const response = await fetchData<never>(
      url,
      Api.ISAAC,
      {handlerType: IsaacToastWrapperType.Full},
      ApiHttpVerbs.PUT,
      {},
      JSON.stringify({
        calculationId: selectedCalculationId,
        mastId,
        drillholes,
      }),
    );

    return response;
  }, [fetchData]);

  const getCalculationNoteFoundationResultsById = useCallback(async (
    calculationId: string,
  ): Promise<ApiResponse<Record<string, CalculationNoteFoundationLoadCase[]>>> => {
    const url = `${process.env.REACT_APP_ISAAC_API_URL}/calculations/${calculationId}/noteFoundationResults`;

    return await fetchData<Record<string, CalculationNoteFoundationLoadCase[]>>(url, Api.ISAAC, {
      useGlobalHandler: false,
    });
  }, [fetchData]);

  return {
    createStartCalculation,
    deleteCalculationByConstructionId,
    getCalculationsByConstructionId,
    getCalculationResults,
    stopCalculationById,
    getConstructionDetails,
    deleteCalculationById,
    downloadExcelInputMatrixById,
    downloadJsonInputMatrixById,
    downloadJsonCalculationConstructionById,
    getCalculationNoteMatGroundTypesById,
    updateDrillholesCode,
    validateDrillholesCode,
    getCalculationNoteFoundationResultsById,
    getLatestCalculationByConstructionId,
    getAcceptedCalculationByConstructionId,
  };
};

export default useCalculationNewHook;
